import { useApi } from '@/providers/apiProvider';
import { AddUserRequest, UpdateUserRequest } from '@/types/user';
import { useMutation } from '@tanstack/react-query';
import axios from 'axios';
import { API_BASE_URL } from '../baseUrl';

export const unauthenticatedInstance = axios.create({
  baseURL: API_BASE_URL,
});

export const useLogin = (
  onSuccess: (data: { access: string; refresh: string }) => void,
  onError: (error: Error) => void
) => {
  const httpClient = unauthenticatedInstance;

  const login = async (email: string, password: string) => {
    const response = await httpClient.post('/token/', {
      email,
      password,
    });
    return response.data;
  };

  return useMutation({
    mutationFn: (data: { email: string; password: string }) =>
      login(data.email, data.password),
    onSuccess,
    onError,
  });
};

export const useRefreshToken = (
  onSuccess: (data: { access: string }) => void,
  onError: (error: Error) => void
) => {
  const httpClient = unauthenticatedInstance;

  const refreshToken = async (token: string) => {
    const response = await httpClient.post('/token/refresh/', {
      refresh: token,
    });
    return response.data;
  };

  return useMutation({
    mutationFn: refreshToken,
    onSuccess,
    onError,
  });
};

export const useForgotPassword = (
  onSuccess: (data: { access: string }) => void,
  onError: (error: Error) => void
) => {
  const httpClient = unauthenticatedInstance;

  const forgotPassword = async (email: string) => {
    const response = await httpClient.post('/users/password-reset/', {
      email,
    });
    return response.data;
  };

  return useMutation({
    mutationFn: forgotPassword,
    onSuccess,
    onError,
  });
};

export const useResetPassword = (
  onSuccess: (data: { access: string }) => void,
  onError: (error: Error) => void
) => {
  const httpClient = unauthenticatedInstance;

  const resetPassword = async (
    uid: string,
    token: string,
    password: string,
    confirmPassword: string
  ) => {
    const response = await httpClient.post('/users/password-reset/confirm/', {
      uid,
      token,
      new_password: password,
      new_password_confirm: confirmPassword,
    });
    return response.data;
  };

  return useMutation({
    mutationFn: (data: {
      uid: string;
      token: string;
      password: string;
      confirmPassword: string;
    }) =>
      resetPassword(data.uid, data.token, data.password, data.confirmPassword),
    onSuccess,
    onError,
  });
};

export const useAddUser = (
  onSuccess: (data: { access: string }) => void,
  onError: (error: Error) => void
) => {
  const httpClient = useApi();

  const addUser = async (data: AddUserRequest) => {
    const response = await httpClient.post('/users/', data);
    return response.data;
  };

  return useMutation({
    mutationFn: addUser,
    onSuccess,
    onError,
  });
};

export const useUpdateUser = () => {
  const httpClient = useApi();

  const updateUser = async (data: UpdateUserRequest) => {
    const response = await httpClient.put(`/users/${data.id}/`, data);
    return response.data;
  };

  return useMutation({
    mutationFn: updateUser,
  });
};

type DeleteUserRequest = {
  id: string;
  password: string;
};

export const useDeleteUser = () => {
  const httpClient = useApi();

  const deleteUser = async (data: DeleteUserRequest) => {
    const response = await httpClient.delete(`/users/${data.id}/`, {
      data: {
        password: data.password,
      },
    });

    return response.data;
  };

  return useMutation({
    mutationFn: deleteUser,
  });
};
